import React, { useCallback } from "react";
import set from "ramda/src/set";
import over from "ramda/src/over";
import map from "ramda/src/map";
import reject from "ramda/src/reject";
import append from "ramda/src/append";
import concat from "ramda/src/concat";
import propEq from "ramda/src/propEq";
import lensPath from "ramda/src/lensPath";
import { createContext } from "../../helpers/createContext";
import { normalize } from "../../helpers/normalize";
import useAuth from "../../routes/login/AuthContext";
import * as API from "../../services/PlacementWorksheetApi";
import { useSelectedNetworkContext } from "../NetworkContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import { propIdEq } from "../../helpers/propIdEq";
import { propId } from "../../helpers/propId";

interface IPlacementWorksheetContext {
  l2Id?: number;
  l3Id?: number;
  isLoading: boolean;
  matchGroupAtLimit: boolean;
  setL2Id: React.Dispatch<React.SetStateAction<number | undefined>>;
  setL3Id: React.Dispatch<React.SetStateAction<number | undefined>>;
  allowedCount: number;
  setAllowedCount: React.Dispatch<React.SetStateAction<number>>;
  occupants?: API.IPlacementWorksheetOccupants;
  fetchPlacementWorksheet: () => Promise<void>;
  updateUnitNumber: (unitId: number, unitNumber: string) => void;
  clearUnit: (unitId: number) => void;
  updateUnitComment: (unitId: number, occupants: string | null) => void;
  removeOccupantFromUnit: (unitId: number, occupantId: string) => void;
  updateUnitOccupantRoomLabelId: (unitId: number, occupantId: string, roomLabelId: number | null) => void;
  updateUnitOccupants: (unitId: number, newOccupants: API.IPlacementWorksheetUnitOccupant[]) => void;
}

interface IPlacementWorksheetSettingsContext extends API.IPlacementWorksheetSettings {
  updateColor: (label: string, value: string) => void;
  updateRoomLabel: (id: number, value: string) => void;
}

const [PlacementWorksheetContext, usePlacementWorksheetContext] = createContext<IPlacementWorksheetContext>();
const [
  PlacementWorksheetSettingsContext,
  usePlacementWorksheetSettingsContext,
] = createContext<IPlacementWorksheetSettingsContext>();

const INITIAL_SETTINGS: API.IPlacementWorksheetSettings = {
  labels: [],
  colors: {
    newResidentColor: null,
    renewingResidentColor: null,
    renewalTransferResidentColor: null,
  },
};

const INITIAL_OCCUPANTS = {
  assignedUnits: [],
  unassignedUnits: [],
};

const INITIAL_UNIT = {
  comment: null,
  unitId: null,
  unitNumber: null,
};

// TODO: propably move state to useReducer
export const PlacementWorksheetProvider: React.FC = (props) => {
  const { selectedNetworkId } = useSelectedNetworkContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar } = useSnackbar();
  const [l2Id, setL2Id] = React.useState<number>();
  const [l3Id, setL3Id] = React.useState<number>();
  const [allowedCount, setAllowedCount] = React.useState<number>(12); // Todo: specify the default value
  const [matchGroupAtLimit, setMatchGroupAtLimit] = React.useState<boolean>(false);
  const [settings, setSettings] = React.useState<API.IPlacementWorksheetSettings>(INITIAL_SETTINGS);
  const [occupants, setOccupants] = React.useState<API.IPlacementWorksheetOccupants>(INITIAL_OCCUPANTS);

  const stopLoading = () => setIsLoading(false);

  const updateUnitComment = (unitId: number, comment: string | null) => {
    if (!occupants || !occupants.assignedUnits) return;

    const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

    if (unitIndex === -1) return;

    setOccupants(set(lensPath(["assignedUnits", unitIndex, "comment"]), comment));
  };

  const updateUnitNumber = (unitId: number, unitNumber: string) => {
    if (!occupants || !occupants.assignedUnits) return;

    const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

    if (unitIndex === -1) return;

    setOccupants(set(lensPath(["assignedUnits", unitIndex, "unitNumber"]), unitNumber));
  };

  const updateUnitOccupants = (unitId: number, newOccupants: API.IPlacementWorksheetUnitOccupant[]) => {
    if (!occupants || !occupants.assignedUnits || !occupants.unassignedUnits) return;

    const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

    if (unitIndex === -1) return;

    setOccupants(over(lensPath(["assignedUnits", unitIndex, "occupants"]), concat(newOccupants)));

    // @ts-ignore
    const occupantsIds = newOccupants.map(propId);

    setOccupants(
      over(
        lensPath(["unassignedUnits"]),
        map(
          over(
            lensPath(["occupants"]),
            reject<API.IPlacementWorksheetUnitOccupant>(({ id }: any) => occupantsIds.includes(id))
          )
        )
      )
    );
  };

  const removeOccupantFromUnit = (unitId: number, occupantId: string) => {
    if (!occupants || !occupants.assignedUnits || !occupants.unassignedUnits) return;

    const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

    if (unitIndex === -1) return;

    const occupantToRemove = occupants.assignedUnits[unitIndex].occupants.find(propIdEq(occupantId));

    if (!occupantToRemove) return;

    setOccupants(over(lensPath(["assignedUnits", unitIndex, "occupants"]), reject(propIdEq(occupantId))));
    setOccupants(over(lensPath<any>(["unassignedUnits"]), append({ ...INITIAL_UNIT, occupants: [occupantToRemove] })));
  };

  const clearUnit = useCallback(
    (unitId: number) => {
      if (!occupants || !occupants.assignedUnits || !occupants.unassignedUnits) return;

      const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

      if (unitIndex === -1) return;

      const removedOccupants = occupants.assignedUnits[unitIndex].occupants;

      setOccupants(set(lensPath(["assignedUnits", unitIndex, "occupants"]), []));
      setOccupants(over(lensPath<any>(["unassignedUnits"]), append({ ...INITIAL_UNIT, occupants: removedOccupants })));
    },
    [normalize(occupants), setOccupants]
  );

  const updateUnitOccupantRoomLabelId = (unitId: number, occupantId: string, roomLabelId: number | null) => {
    if (!occupants || !occupants.assignedUnits) return;

    const unitIndex = occupants.assignedUnits.findIndex(propEq("unitId", unitId));

    if (unitIndex === -1) return;

    const occupantIndex = occupants.assignedUnits[unitIndex]?.occupants?.findIndex(propEq("id", occupantId));

    if (occupantIndex === undefined || occupantIndex === -1) return;

    setOccupants(set(lensPath(["assignedUnits", unitIndex, "occupants", occupantIndex, "roomLabelId"]), roomLabelId));
  };

  const updateColor = (label: string, value: string) => {
    setSettings(set(lensPath(["colors", label]), value));
  };

  const updateRoomLabel = (id: number, value: string) => {
    const labelIndex = settings.labels.findIndex(propEq("id", id));

    if (labelIndex !== -1) {
      setSettings(set(lensPath(["labels", labelIndex, "label"]), value));
    }
  };

  const handleError = () => {
    showErrorSnackbar("An Error Occurred");
  };

  const settingsValue = React.useMemo(
    () => ({
      ...settings,
      updateColor,
      updateRoomLabel,
    }),
    [normalize(settings)]
  );

  const updateLocalState = (data?: API.IPlacementWorksheetRes) => {
    if (!data) return;
    setMatchGroupAtLimit(data.matchGroupAtLimit);
    setSettings(data.settings);
    setOccupants(data.occupants);
  };

  const fetchPlacementWorksheet = React.useCallback(async () => {
    if (!selectedNetworkId || !l2Id || !l3Id) return;
    setIsLoading(true);
    return API.fetchPlacementWorksheet(selectedNetworkId, l2Id, l3Id, await getAuthToken())
      .then(updateLocalState)
      .catch(handleError)
      .finally(stopLoading);
  }, [selectedNetworkId, l2Id, l3Id]);

  // Reset occupants when l3Id changes
  React.useEffect(() => {
    // Reset occupants to initial state when l3Id changes
    setOccupants(INITIAL_OCCUPANTS);
  }, [l3Id]);

  const mainValue = React.useMemo(
    () => ({
      l3Id,
      l2Id,
      clearUnit,
      isLoading,
      setL2Id,
      setL3Id,
      allowedCount,
      matchGroupAtLimit,
      setAllowedCount,
      occupants,
      updateUnitComment,
      updateUnitOccupantRoomLabelId,
      updateUnitOccupants,
      fetchPlacementWorksheet,
      removeOccupantFromUnit,
      updateUnitNumber,
    }),
    [l3Id, l2Id, allowedCount, normalize(occupants), fetchPlacementWorksheet, isLoading, clearUnit, matchGroupAtLimit]
  );

  React.useEffect(() => {
    fetchPlacementWorksheet();
  }, [fetchPlacementWorksheet]);

  return (
    <PlacementWorksheetContext.Provider value={mainValue}>
      <PlacementWorksheetSettingsContext.Provider value={settingsValue}>
        {props.children}
      </PlacementWorksheetSettingsContext.Provider>
    </PlacementWorksheetContext.Provider>
  );
};

export { usePlacementWorksheetContext, usePlacementWorksheetSettingsContext };
